import React, { forwardRef } from 'react';
import { Tooltip } from '@/components/ui/beui';

export interface ToolButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required — every tool button is icon-only, so it needs an accessible name. */
  label: string;
  /** Renders the pressed capsule. Also sets aria-pressed. */
  active?: boolean;
  /** `toggle` reports aria-pressed; `action` and `menu` don't. */
  kind?: 'action' | 'toggle' | 'menu';
  /** Opt-in colour shifts for the AI and destructive tools. */
  tone?: 'default' | 'ai' | 'danger';
  /** Set on a trigger that opens a sub-bar. */
  expanded?: boolean;
  /** Tooltip placement. The bars all sit at the bottom of the canvas, so `top`. */
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * The single tool button used by every editor bar. Centralising it means the
 * glass-capsule restyle lands in one place rather than 30 copies of a className.
 */
export const ToolButton = forwardRef<HTMLButtonElement, ToolButtonProps>(function ToolButton(
  { label, active = false, kind = 'action', tone = 'default', expanded, tooltipSide = 'top', className = '', children, ...rest },
  ref,
) {
  const toneHover =
    tone === 'danger'
      ? 'hover:text-destructive hover:bg-destructive/10'
      : tone === 'ai'
        ? 'bg-gradient-to-r from-purple-600/0 to-blue-600/0 hover:from-primary hover:to-primary hover:text-primary-foreground duration-200'
        : 'hover:text-primary hover:bg-muted';

  return (
    // The wrapper span is the tooltip's anchor and the flex item the pill track
    // lays out — `flex` (not the default inline-flex) so the button keeps its
    // exact footprint in the bar.
    <Tooltip content={label} side={tooltipSide} wrapperClassName="flex">
      <button
        ref={ref}
        type="button"
        aria-label={label}
        aria-pressed={kind === 'toggle' ? active : undefined}
        aria-expanded={kind === 'menu' ? expanded : undefined}
        data-active={active || undefined}
        className={[
          // No `transition-all` here — ss-press specifies exact properties,
          // duration and the custom ease-out curve.
          'relative flex items-center justify-center whitespace-nowrap p-2 rounded-full ss-press',
          // Disabled buttons swallow pointer events, so let them fall through to
          // the tooltip's wrapper — hover still shows the label.
          'disabled:pointer-events-none disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground',
          active ? 'text-primary bg-surface-primary shadow-sm' : 'text-muted-foreground',
          toneHover,
          className,
        ].join(' ')}
        {...rest}
      >
        {children}
      </button>
    </Tooltip>
  );
});
